import { useMemo, useRef } from 'react';
import {
	BN,
	BigNum,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	SpotBalanceType,
	StrictOraclePrice,
	ZERO,
	calculateAssetWeight,
	calculateLiabilityWeight,
	getWorstCaseTokenAmounts,
} from '@drift-labs/sdk';
import { ENUM_UTILS, matchEnum } from '@drift/common';
import useDriftClient from './useDriftClient';
import useDriftClientIsReady from './useDriftClientIsReady';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import useDriftAccountStore from '../stores/useDriftAccountsStore';
import NumLib from '../utils/NumLib';
import { UserBalanceInfo } from '../components/UserBalancesPortfolioTable/UserBalancesPortfolioTable';
import { useShallow } from 'zustand/react/shallow';
import { useSpotMarketsLookup } from './spotMarkets/useSpotMarketsLookup';

interface UseBalanceRowDataParams {
	spotMarketIndex: number;
	highlighted?: boolean;
}

const valuesAreEqual = (
	a: UserBalanceInfo | null,
	b: UserBalanceInfo | null
) => {
	if (!a || !b) {
		return false;
	}

	return (
		a.highlighted === b.highlighted &&
		a.spotMarketIndex === b.spotMarketIndex &&
		a.symbol === b.symbol &&
		a.depositsBase.eq(b.depositsBase) &&
		a.depositsQuote.eq(b.depositsQuote) &&
		a.borrowsBase.eq(b.borrowsBase) &&
		a.borrowsQuote.eq(b.borrowsQuote) &&
		a.netBase.eq(b.netBase) &&
		a.netQuote.eq(b.netQuote) &&
		a.liqPrice.eq(b.liqPrice) &&
		a.accountNum === b.accountNum &&
		a.assetWeight.eq(b.assetWeight) &&
		a.liabilityWeight.eq(b.liabilityWeight) &&
		a.defaultAssetWeight === b.defaultAssetWeight &&
		a.defaultLiabilityWeight === b.defaultLiabilityWeight &&
		a.isLoweredAssetWeight === b.isLoweredAssetWeight &&
		a.scaleInitialAssetWeightStart.eq(b.scaleInitialAssetWeightStart) &&
		a.poolId === b.poolId
	);
};

export const useBalanceRowData = ({
	spotMarketIndex,
	highlighted = false,
}: UseBalanceRowDataParams): UserBalanceInfo | null => {
	const driftClientIsReady = useDriftClientIsReady();
	const driftClient = useDriftClient();

	const market = useSpotMarketsLookup()[spotMarketIndex];

	const borrowLendInfo = useDriftStore(
		useShallow((s) =>
			s.borrowLendData?.find((mkt) => mkt.bankIndex === spotMarketIndex)
		)
	);

	const currentUserClient = useDriftAccountStore(
		useShallow((s) => s.getCurrentUserAccount()?.client)
	);

	const relevantBalances = useDriftAccountStore(
		useShallow((s) => {
			const balances = s.getCurrentUserAccount()?.spotBalances ?? [];
			return balances.filter(
				(bal) => bal.asset.marketIndex === spotMarketIndex
			);
		})
	);

	// Memoize the relevant balances to prevent unnecessary recalculations
	const memoizedRelevantBalances = useMemo(() => {
		return relevantBalances;
	}, [
		// Only update memoized value when actual balance values change, not the array reference
		relevantBalances
			.map(
				(bal) =>
					bal.balance.toString() +
					',' +
					bal.quoteValue.toString() +
					',' +
					ENUM_UTILS.toStr(bal.balanceType) +
					',' +
					bal.accountId
			)
			.join(','),
	]);

	const spotPosition = useMemo(() => {
		return currentUserClient
			?.getActiveSpotPositions()
			?.find((pos) => pos.marketIndex === spotMarketIndex);
	}, [currentUserClient, spotMarketIndex]);

	const oraclePriceData = useMemo(() => {
		return driftClient?.getOracleDataForSpotMarket(spotMarketIndex);
	}, [driftClient, spotMarketIndex]);

	const maxMarginRatio = useMemo(() => {
		return currentUserClient?.getUserAccount().maxMarginRatio;
	}, [currentUserClient]);

	const liquidationPrice = useMemo(() => {
		return currentUserClient?.spotLiquidationPrice(spotMarketIndex);
	}, [currentUserClient, spotMarketIndex]);

	const previousValue = useRef<UserBalanceInfo | null>(null);

	const newValue = useMemo(() => {
		if (
			!driftClientIsReady ||
			!currentUserClient?.isSubscribed ||
			!driftClient ||
			!borrowLendInfo
		) {
			return null;
		}

		const marketAccount = driftClient.getSpotMarketAccount(spotMarketIndex);
		if (!marketAccount) {
			return null;
		}

		const strictOraclePrice = new StrictOraclePrice(
			oraclePriceData?.price ?? ZERO
		);

		const {
			weight: worstCaseWeightInitial,
			weightedTokenValue: weightedTokenValueInitial,
		} = spotPosition
			? getWorstCaseTokenAmounts(
					spotPosition,
					marketAccount,
					strictOraclePrice,
					'Initial',
					maxMarginRatio
				)
			: { weight: null, weightedTokenValue: null };

		const depositBalances = memoizedRelevantBalances.filter((bal) =>
			matchEnum(bal.balanceType, SpotBalanceType.DEPOSIT)
		);

		const borrowBalances = memoizedRelevantBalances.filter((bal) =>
			matchEnum(bal.balanceType, SpotBalanceType.BORROW)
		);

		const balancesSum = NumLib.sumBigNums(
			memoizedRelevantBalances.map((bal) => bal.balance),
			market.precisionExp
		);

		const depositsBase = NumLib.sumBigNums(
			depositBalances.map((depos) => depos.balance),
			market.precisionExp
		);

		const depositsQuote = NumLib.sumBigNums(
			depositBalances.map((depos) => depos.quoteValue),
			QUOTE_PRECISION_EXP
		);

		const borrowsBase = NumLib.sumBigNums(
			borrowBalances.map((depos) => depos.balance),
			market.precisionExp
		);

		const borrowsQuote = NumLib.sumBigNums(
			borrowBalances.map((depos) => depos.quoteValue),
			QUOTE_PRECISION_EXP
		);

		const oraclePriceBigNum = BigNum.from(
			strictOraclePrice.current,
			PRICE_PRECISION_EXP
		);

		const assetWeight =
			weightedTokenValueInitial !== null && weightedTokenValueInitial.gt(ZERO)
				? worstCaseWeightInitial?.div(new BN(100))
				: calculateAssetWeight(
						balancesSum.val,
						oraclePriceBigNum.val,
						marketAccount,
						'Initial'
					)?.div(new BN(100));

		const liabilityWeight =
			weightedTokenValueInitial !== null && weightedTokenValueInitial.lt(ZERO)
				? worstCaseWeightInitial?.div(new BN(100))
				: calculateLiabilityWeight(
						balancesSum.val,
						marketAccount,
						'Initial'
					)?.div(new BN(100));

		const scaleInitialAssetWeightStartBigNum = BigNum.from(
			marketAccount.scaleInitialAssetWeightStart,
			QUOTE_PRECISION_EXP
		);
		const totalDepositsQuote =
			borrowLendInfo?.totalDepositsQuote ?? BigNum.zero();
		const isLoweredAssetWeight =
			totalDepositsQuote.gt(scaleInitialAssetWeightStartBigNum) &&
			!scaleInitialAssetWeightStartBigNum.eqZero();

		return {
			highlighted,
			spotMarketIndex: marketAccount.marketIndex,
			symbol: market.symbol,
			depositsBase,
			depositsQuote,
			borrowsBase,
			borrowsQuote,
			netBase: depositsBase.sub(borrowsBase),
			netQuote: depositsQuote.sub(borrowsQuote),
			liqPrice: BigNum.from(liquidationPrice, PRICE_PRECISION_EXP),
			accountNum: depositBalances.length + borrowBalances.length,
			assetWeight: BigNum.from(assetWeight),
			liabilityWeight: BigNum.from(liabilityWeight),
			defaultAssetWeight: borrowLendInfo.assetWeight,
			defaultLiabilityWeight: borrowLendInfo.liabilityWeight,
			isLoweredAssetWeight,
			scaleInitialAssetWeightStart: scaleInitialAssetWeightStartBigNum,
			poolId: market.poolId,
		};
	}, [
		driftClientIsReady,
		currentUserClient?.isSubscribed,
		driftClient,
		borrowLendInfo,
		spotMarketIndex,
		market.symbol,
		market.precisionExp,
		market.poolId,
		// Use memoized balances to prevent unnecessary recalculations
		memoizedRelevantBalances,
		spotPosition,
		// Memoize oracle price to reduce frequency of updates
		oraclePriceData?.price?.toString(),
		maxMarginRatio,
		liquidationPrice,
		highlighted,
	]);

	if (valuesAreEqual(previousValue.current, newValue)) {
		return previousValue.current;
	}

	previousValue.current = newValue;
	return newValue;
};
